import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box,
  Typography, 
  CircularProgress, 
  Tooltip, 
  IconButton,
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';


const Permissions = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "Permissions", "create", false);
  const canEdit = hasRight(permissionMap, "Permissions", "update", false);
  const canDelete = hasRight(permissionMap, "Permissions", "delete", false);
  
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


  useEffect(() => {
    const handlePermissions = async () => {
      try {
        setLoading(true);
        const res = await api.get('/permissions');
        setPermissions(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    handlePermissions();
  }, []);

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(isMobile ? { width: 60 } : { flex: 1 }),
    },
    {
      field: "element",
      headerName: "Element",
      ...(isMobile ? { width: 120 } : { flex: 2 }),
    },
    {
      field: "path",
      headerName: "Path",
      ...(isMobile ? { width: 120 } : { flex: 2 }),
    },
    {
      field: "description",
      headerName: "Description",
      ...(isMobile ? { width: 200 } : { flex: 3.5 }),
    },
    {
      field: "category",
      headerName: "Category",
      ...(isMobile ? { width: 80 } : { flex: 1.2 }),
    },
    {
      field: 'right',
      headerName: 'Right',
      ...(isMobile ? { width: 100 } : { flex: 1.5 }),
      renderCell: (params) => (
        <Box
          sx={{
            height: '100%',
            alignItems: 'flex-start',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {params.row?.right.map((item, index) => (
            <Box key={index} display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                {item.rightName}
              </Typography>
            </Box>
          ))}
        </Box>
      ),
    },
    {
      field: "sortOrder",
      headerName: "SortOrder",
      align: "right",
      ...(isMobile ? { width: 80 } : { flex: 1 }),
    },
    {
      field: "isActive",
      headerName: "IsActive",
      ...(isMobile ? { width: 80 } : { flex: 1.2 }),
    },
    {
      field: "isInvisible",
      headerName: "IsInvisible",
      ...(isMobile ? { width: 80 } : { flex: 1.2 }),
    },
  ], [isMobile]);

  const actionColumn = useMemo(() => {
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            sx={{ width: '100%', height: '100%' }}
          >
            {canEdit && (
              <Tooltip title="Edit">
                <IconButton
                  aria-label="Edit"
                  sx={{
                    color: theme.palette.primary.dark,
                    '&:hover': { color: theme.palette.green.deep },
                  }}
                  onClick={() => { if (!canEdit) return; navigate(`/permissions/${params.row._id}`); }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  aria-label="Delete"
                  sx={{
                    color: theme.palette.primary.dark,
                    '&:hover': { color: theme.palette.green.deep },
                  }}
                  onClick={() => { if (!canDelete) return; handleDeleteClick(params.row._id); }}
                >
                  <DeleteForever />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ];
  }, [navigate, theme.palette, canEdit, canDelete]);

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, permissions);
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        await api.delete(`/permissions/delete/${idToDelete}`);
        setPermissions(permissions.filter((item) => item._id !== idToDelete));
        toast.success('Permission deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, permissions);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }
        const res = await api.patch('/permissions/soft-delete', { ids: selectedIds });
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        setPermissions(permissions.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error("Failed to delete: " + err.message);
    }
  }, [canDelete, deleteType, idToDelete, rowSelectionModel, permissions]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Permissions"
          subtitle={`${loading ? 'Loading...' : 'List of Permissions'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, permissions)}
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/permissions/new")}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">
            Error: {error?.message || ''}
          </Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <CardList
                rows={permissions}
                primaryField="element"
                secondaryField="path"
                fields={[
                  { label: "Description", key: "description" },
                  { label: "Category", key: "category" },
                  { label: "Rights", key: "right" },
                  { label: "Sort Order", key: "sortOrder" },
                  { label: "Is Invisible", key: "isInvisible" },
                ]}
                isActiveField="isActive"
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/permissions/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={permissions || []}
                columns={columns.concat(actionColumn)}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  onRowSelectionModelChange: setRowSelectionModel,
                  rowSelectionModel,
                  initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                }}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default Permissions;
